import { UserSession } from "@domain/entities";
import { create } from "zustand";

/**
 * Zustand store for managing user session state.
 */
interface UserState {
  userSession: UserSession;

  /**
   * Sets the full user session.
   * @param username - The username of the user.
   * @param accessToken - The access token for authentication.
   * @param accessToken -  the xsrfToken for authentication.
   */
  setSessionState: (username: string | null, accessToken: string | null, xsrfToken: string | null) => void;

  /**
   * Updates only the access token in the session.
   * @param token - The new access token.
   */
  setAccessToken: (token: string | null) => void;

  /**
   * Updates only the username in the session.
   * @param username - The new username.
   */
  setUsername: (username: string | null) => void;

  /**
   * Updates only the xsrfToken in the session.
   * @param username - The new xsrfToken.
   */
  setXSRFToken: (xsrfToken: string | null) => void;

  /**
   * Clears the user session.
   */
  clear: () => void;
}

export const useUserManagementStore = create<UserState>((set) => ({
  userSession: new UserSession(null, null),

  setSessionState: (username, accessToken, xsrfToken) =>
    set(() => ({
      userSession: new UserSession(username, accessToken, xsrfToken),
    })),

  setAccessToken: (token) =>
    set((state) => ({
      userSession: new UserSession(state.userSession?.username ?? null, token),
    })),

  setUsername: (username) =>
    set((state) => ({
      userSession: new UserSession(username, state.userSession?.accessToken ?? null),
    })),

  setXSRFToken: (xsrfToken) =>
    set((state) => ({
      userSession: new UserSession(state.userSession?.username ?? null, state.userSession?.accessToken ?? null, xsrfToken),
    })),

  clear: () =>
    set(() => ({
      userSession: new UserSession(null, null, null),
    })),
}));
